import emailjs from '@emailjs/browser';
import { useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import SEO from '../SEO/SEO';

const Contact = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');
  const [captchaValue, setCaptchaValue] = useState(null);
  const recaptchaRef = useRef(null);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.id]: e.target.value
    });
  };

  const handleCaptchaChange = (value) => {
    setCaptchaValue(value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check if reCAPTCHA is completed
    if (!captchaValue) {
      setSubmitStatus('captcha-error');
      return;
    }
    setIsSubmitting(true);
    setSubmitStatus('');

    try {
      // Using environment variables for EmailJS credentials
      await emailjs.send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
        {
          from_name: `${formData.firstName} ${formData.lastName}`,
          from_email: formData.email,
          phone: formData.phone,
          company: formData.company,
          message: formData.message,
          to_name: 'TerraNova Consulting',
          'g-recaptcha-response': captchaValue, // Include captcha value
        },
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY
      );

      setSubmitStatus('success');
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        company: '',
        message: ''
      });
      
      // Reset reCAPTCHA
      setCaptchaValue(null);
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <SEO 
        title="Contact Us - Get In Touch"
        description="Contact TerraNova Consulting for your geotechnical engineering needs. Get expert consultation for site investigation, foundation engineering, and soil analysis. 24/7 emergency services available."
        keywords="contact geotechnical engineer, site investigation consultation, foundation engineering quote, soil analysis contact, geotechnical emergency services"
        url="/contact"
      />
      <div className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-5xl font-bold text-TerraNova-charcoal mb-6">
              Contact Us
            </h1>
            <p className="text-xl text-TerraNova-slate max-w-3xl mx-auto">
              Ready to discuss your geoenvironmental and/or geotechnical needs? Get in touch with us today
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12">
            <div className="bg-white rounded-lg p-8 border-2 border-TerraNova-cream shadow-lg">
              <h2 className="text-2xl font-bold text-TerraNova-charcoal mb-6">Send Us a Message</h2>
              
              {/* Success/Error Messages */}
              {submitStatus === 'success' && (
                <div className="mb-6 p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg">
                  Thank you! Your message has been sent successfully. We'll aim to get back to you within 24 hours.
                </div>
              )}
              {submitStatus === 'error' && (
                <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
                  Sorry, there was an error sending your message. Please try again or contact us directly.
                </div>
              )}
              {submitStatus === 'captcha-error' && (
                <div className="mb-6 p-4 bg-yellow-100 border border-yellow-400 text-yellow-700 rounded-lg">
                  Please complete the reCAPTCHA verification before submitting the form.
                </div>
              )}

              <form className="space-y-6" onSubmit={handleSubmit}>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="firstName" className="block text-sm font-medium text-TerraNova-charcoal mb-2">
                      First Name *
                    </label>
                    <input
                      type="text"
                      id="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border-2 border-TerraNova-cream rounded-lg focus:border-TerraNova-rust focus:outline-none transition duration-200"
                      placeholder="John"
                    />
                  </div>
                  <div>
                    <label htmlFor="lastName" className="block text-sm font-medium text-TerraNova-charcoal mb-2">
                      Last Name *
                    </label>
                    <input
                      type="text"
                      id="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border-2 border-TerraNova-cream rounded-lg focus:border-TerraNova-rust focus:outline-none transition duration-200"
                      placeholder="Doe"
                    />
                  </div>
                </div>
                
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-TerraNova-charcoal mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border-2 border-TerraNova-cream rounded-lg focus:border-TerraNova-rust focus:outline-none transition duration-200"
                    placeholder="john.doe@example.com"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-TerraNova-charcoal mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border-2 border-TerraNova-cream rounded-lg focus:border-TerraNova-rust focus:outline-none transition duration-200"
                    placeholder="07700777140"
                  />
                </div>

                <div>
                  <label htmlFor="company" className="block text-sm font-medium text-TerraNova-charcoal mb-2">
                    Company/Organization
                  </label>
                  <input
                    type="text"
                    id="company"
                    value={formData.company}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border-2 border-TerraNova-cream rounded-lg focus:border-TerraNova-rust focus:outline-none transition duration-200"
                    placeholder="Your Company Name"
                  />
                </div>
                
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-TerraNova-charcoal mb-2">
                    Requirements *
                  </label>
                  <textarea
                    id="message"
                    rows={6}
                    value={formData.message}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border-2 border-TerraNova-cream rounded-lg focus:border-TerraNova-rust focus:outline-none transition duration-200"
                    placeholder="Please describe your requirments, timeline, and any specifics..."
                  ></textarea>
                </div>

                {/* reCAPTCHA */}
                <div className="flex justify-center">
                  <ReCAPTCHA
                    ref={recaptchaRef}
                    sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                    onChange={handleCaptchaChange}
                    theme="light"
                  />
                </div>
                <button
                  type="submit"
                  disabled={isSubmitting || !captchaValue}
                  className={`w-full py-3 px-6 rounded-lg font-semibold transition duration-300 shadow-lg ${
                    isSubmitting || !captchaValue
                      ? 'bg-gray-400 text-gray-600 cursor-not-allowed' 
                      : 'bg-TerraNova-rust text-white hover:bg-TerraNova-clay'
                  }`}
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </button>
              </form>
            </div>

            {/* Contact Information */}
            <div className="bg-gradient-to-br from-TerraNova-cream to-TerraNova-sand rounded-xl p-8">
              <h2 className="text-2xl font-bold text-TerraNova-charcoal mb-6">Get In Touch</h2>
              
              <div className="space-y-8">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-TerraNova-rust rounded-lg flex items-center justify-center flex-shrink-0">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-TerraNova-charcoal mb-1">Office Location</h3>
                    <p className="text-TerraNova-slate">Jersey, Channel Islands</p>
                  </div>
                </div>

                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-TerraNova-rust rounded-lg flex items-center justify-center flex-shrink-0">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-TerraNova-charcoal mb-1">Phone</h3>
                    <a 
                      href="tel:07700777140"
                      className="text-TerraNova-rust hover:text-TerraNova-clay transition duration-200 font-medium"
                    >
                      07700777140
                    </a>
                  </div>
                </div>

                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-TerraNova-rust rounded-lg flex items-center justify-center flex-shrink-0">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 7.89a2 2 0 002.83 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-TerraNova-charcoal mb-1">Email</h3>
                    <a 
                      href="mailto:Isaac@TerraNova.je"
                      className="text-TerraNova-rust hover:text-TerraNova-clay transition duration-200 font-medium"
                    >
                      Isaac@TerraNova.je
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Contact;
